// GraphQL
import { client } from '../client';
import i18n from '../../i18n';
import { GET_ORDER_BY_ID } from '../queries/orders/orders';
import {
  GET_CONCESSION_BY_ID,
  GET_ROAD_BY_ID,
} from '../queries/geoPositions/roads';
import {
  GET_COMMUNICATION_BY_ID,
  GET_INTERLOCUTOR_GROUP_BY_ID,
  GET_MEDIA_OF_COMMUNICATION_BY_ID,
} from '../queries/communication';
import {
  GET_CAPITOL_BY_ID,
  GET_CLASSIFICATION_BY_ID_OR_ROID,
  GET_SUB_CAPITOL_BY_ID,
  FETCH_PREDEFINED_ORDERS,
} from '../queries/orders/predefinedData';
import { GET_USER_BY_ID, FETCH_USERS } from '../queries/users';
import { warmupOrdersByIds } from './warmupOrders';

// Utils
import { laneLabel, trackLabel } from '../../utils/catalogLabels';
import { pkFormatter } from '../../utils/transform';

// This is for summary data
export const selectCommunicationOrderByIdOrder = async (
  orderId: string,
  reFetch = false
) => {
  const {
    data: { order },
  } = await client.query({
    query: GET_ORDER_BY_ID,
    variables: {
      id: orderId,
    },
    fetchPolicy: reFetch ? 'network-only' : 'cache-first',
  });
  const parentExpeditionOrder = await client.query({
    query: GET_ORDER_BY_ID,
    variables: {
      id: order.parentExpeditionOrderIDs[0],
    },
    fetchPolicy: reFetch ? 'network-only' : 'cache-first',
  });

  const [
    {
      data: { communication },
    },
    {
      data: { capitol },
    },
    {
      data: { subCapitol },
    },
    {
      data: { classification },
    },
    {
      data: { user },
    },
    {
      data: { predefinedOrders },
    },
  ] = await Promise.all([
    client.query({
      query: GET_COMMUNICATION_BY_ID,
      variables: {
        id: order.communicationIDs[0],
      },
      fetchPolicy: reFetch ? 'network-only' : 'cache-first',
    }),
    client.query({
      query: GET_CAPITOL_BY_ID,
      variables: {
        id: order.capitolIDs[0],
      },
      fetchPolicy: reFetch ? 'network-only' : 'cache-first',
    }),
    client.query({
      query: GET_SUB_CAPITOL_BY_ID,
      variables: {
        id: order.subCapitolIDs[0],
      },
      fetchPolicy: reFetch ? 'network-only' : 'cache-first',
    }),
    client.query({
      query: GET_CLASSIFICATION_BY_ID_OR_ROID,
      variables: {
        id: order.classificationIDs[0],
      },
      fetchPolicy: reFetch ? 'network-only' : 'cache-first',
    }),
    client.query({
      query: GET_USER_BY_ID,
      variables: {
        input: {
          id: order.userId,
        },
      },
      fetchPolicy: reFetch ? 'network-only' : 'cache-first',
    }),
    client.query({
      query: FETCH_PREDEFINED_ORDERS,
      fetchPolicy: 'cache-first',
    }),
  ]);

  // T36: Resolve predefinido name from ID (fallback to communication entity for backwards compatibility)
  const predefinedOrderId = order.predefinedOrderIDs?.[0] || communication.predefinedOrderIDs?.[0];
  const predefinedOrderName = predefinedOrderId
    ? predefinedOrders?.find(
        (p: { id: string; name: string }) => p.id === predefinedOrderId
      )?.name
    : undefined;

  const [
    {
      data: { concession },
    },
    {
      data: { road },
    },
    {
      data: { interlocutorGroup },
    },
    {
      data: { mediaOfCommunication },
    },
  ] = await Promise.all([
    client.query({
      query: GET_CONCESSION_BY_ID,
      variables: {
        id: communication.concessionIDs[0],
      },
      fetchPolicy: reFetch ? 'network-only' : 'cache-first',
    }),
    client.query({
      query: GET_ROAD_BY_ID,
      variables: {
        id: communication.roadIDs[0],
      },
      fetchPolicy: reFetch ? 'network-only' : 'cache-first',
    }),
    client.query({
      query: GET_INTERLOCUTOR_GROUP_BY_ID,
      variables: {
        id: communication.interlocutorGroupIDs[0],
      },
      fetchPolicy: reFetch ? 'network-only' : 'cache-first',
    }),
    client.query({
      query: GET_MEDIA_OF_COMMUNICATION_BY_ID,
      variables: {
        id: communication.mediaOfCommunicationIDs[0],
      },
      fetchPolicy: reFetch ? 'network-only' : 'cache-first',
    }),
  ]);
  return {
    id: order.id,
    communicationID: order.communicationIDs[0], // ID de la entidad Communication para el modal de cambio de estado
    roid: order.roid.split('.'),
    registerInitDate: order.registerInitDate,
    groupInter: interlocutorGroup.name,
    media: mediaOfCommunication.name,
    sendStatus: communication.sendStatus,
    concession: concession.name,
    road: road.name,
    pkInit: pkFormatter(communication.pkInitKm, communication.pkInitMeter),
    pkEnd: pkFormatter(communication.pkEndKm, communication.pkEndMeter),
    description: order.description,
    notes: order.notes,
    capitol: capitol.name,
    subCapitol: subCapitol.name,
    classification: classification.name,
    predefine: predefinedOrderName,
    track: trackLabel(communication.track),
    roadDirections: road.directions,
    direction: communication?.direction,
    margin: communication?.margin,
    lane: laneLabel(communication.lane),
    city: concession.location,
    pkInitRoad: pkFormatter(road.pkInitKm, road.pkInitMeter, true),
    pkEndRoad: pkFormatter(road.pkEndKm, road.pkEndMeter, true),
    state: communication.state || order.state,
    parentOrderId: order.parentExpeditionOrderIDs,
    userOperator: user,
    stateParentExpeditionOrder: parentExpeditionOrder.data.order.state,
  };
};
export const selectCommunicationOrderByIdOrderWithRelationshipIds = async (
  orderId: string,
  reFetch = false
) => {
  const {
    data: { order },
  } = await client.query({
    query: GET_ORDER_BY_ID,
    variables: {
      id: orderId,
    },
    fetchPolicy: reFetch ? 'network-only' : 'cache-first',
  });

  const {
    data: { order: parentOrder },
  } = await client.query({
    query: GET_ORDER_BY_ID,
    variables: {
      id: order.parentExpeditionOrderIDs[0],
    },
    fetchPolicy: reFetch ? 'network-only' : 'cache-first',
  });

  const {
    data: { communication },
  } = await client.query({
    query: GET_COMMUNICATION_BY_ID,
    variables: {
      id: order.communicationIDs[0],
    },
    fetchPolicy: reFetch ? 'network-only' : 'cache-first',
  });

  const [
    {
      data: { concession },
    },
    {
      data: { road },
    },
  ] = await Promise.all([
    client.query({
      query: GET_CONCESSION_BY_ID,
      variables: {
        id: communication.concessionIDs[0],
      },
      fetchPolicy: reFetch ? 'network-only' : 'cache-first',
    }),
    client.query({
      query: GET_ROAD_BY_ID,
      variables: {
        id: communication.roadIDs[0],
      },
      fetchPolicy: reFetch ? 'network-only' : 'cache-first',
    }),
  ]);

  const {
    data: { capitol },
  } = await client.query({
    query: GET_CAPITOL_BY_ID,
    variables: {
      id: order.capitolIDs[0],
    },
    fetchPolicy: reFetch ? 'network-only' : 'cache-first',
  });

  const {
    data: { subCapitol },
  } = await client.query({
    query: GET_SUB_CAPITOL_BY_ID,
    variables: {
      id: order.subCapitolIDs[0],
    },
    fetchPolicy: reFetch ? 'network-only' : 'cache-first',
  });

  // Load parent order details if it's a generic OT or campaign for duplicate functionality
  let relatedOrderData = null;
  if (parentOrder.type === 'generic' || parentOrder.type === 'campaign') {
    try {
      const [
        {
          data: { capitol: parentCapitol },
        },
        {
          data: { subCapitol: parentSubCapitol },
        },
      ] = await Promise.all([
        client.query({
          query: GET_CAPITOL_BY_ID,
          variables: {
            id: parentOrder.capitolIDs[0],
          },
          fetchPolicy: reFetch ? 'network-only' : 'cache-first',
        }),
        client.query({
          query: GET_SUB_CAPITOL_BY_ID,
          variables: {
            id: parentOrder.subCapitolIDs[0],
          },
          fetchPolicy: reFetch ? 'network-only' : 'cache-first',
        }),
      ]);

      relatedOrderData = {
        data: {
          id: parentOrder.id,
          roid: parentOrder.roid,
          capitol: parentCapitol.name,
          capitolId: parentOrder.capitolIDs[0],
          subCapitol: parentSubCapitol.name,
          subCapitolId: parentOrder.subCapitolIDs[0],
        },
        isShowedData: true,
      };
    } catch (error) {
      console.error('Error loading parent order details:', error);
    }
  }

  return {
    id: order.id,
    roid: order.roid.split('.'),
    registerInitDate: order.registerInitDate,
    groupInter: communication.interlocutorGroupIDs[0],
    media: communication.mediaOfCommunicationIDs[0],
    sendStatus: communication.sendStatus,
    concession: communication.concessionIDs[0],
    road: communication.roadIDs[0],
    pkInit: pkFormatter(communication.pkInitKm, communication.pkInitMeter),
    pkEnd: pkFormatter(communication.pkEndKm, communication.pkEndMeter),
    description: order.description,
    notes: order.notes,
    capitol: order.capitolIDs[0],
    subCapitol: order.subCapitolIDs[0],
    classification: order.classificationIDs[0],
    predefine: communication.predefinedOrderIDs?.[0],
    track: communication.track,
    direction: communication?.direction,
    margin: communication?.margin,
    lane: communication.lane,
    city: concession.location,
    pkInitRoad: pkFormatter(road.pkInitKm, road.pkInitMeter, true),
    pkEndRoad: pkFormatter(road.pkEndKm, road.pkEndMeter, true),
    parentOrderType: parentOrder.type,
    parentOrderId: parentOrder.id,
    capitolName: capitol.name,
    subCapitolName: subCapitol.name,
    relatedOrderData,
  };
};

// This is for tables data
export const selectCommunicationOrders = async (
  communications: any[] = [],
  reFetch = false
) => {
  // Resolver creador (Order.userId → "Nombre Apellido") leyendo FETCH_USERS
  // de la caché Apollo. El loader pre-carga FETCH_USERS antes de invocar
  // este selector, así que readQuery da hit. Patrón espejo del usado en
  // selectActionOrders (src/gql/selectors/actions.ts:1259-1270).
  const creatorMap: Record<string, string> = {};
  try {
    const usersData = client.readQuery({ query: FETCH_USERS });
    if (usersData?.users) {
      usersData.users.forEach(
        (u: { id: string; name?: string; surName?: string }) => {
          creatorMap[u.id] = `${u.name ?? ''} ${u.surName ?? ''}`.trim();
        },
      );
    }
  } catch {
    // Cache no disponible — columna Creador queda vacía como fallback
  }

  // Warm-up: UNA query `ordersByIds` puebla la caché con las Orders paralelas
  // de todas las filas; los GET_ORDER_BY_ID per-row de abajo pasan a
  // resolverse de caché (redirect Query.order). Si falla → no-op, per-row
  // intacto como fallback natural.
  await warmupOrdersByIds(
    communications.map((c) => c?.orderIDs?.[0]),
    reFetch
  );

  // Resiliencia POR FILA (paridad con `selectActionOrders` y `selectAllOrders`,
  // que ya la tienen): el guard de Order huérfana de más abajo cubre un solo
  // modo de fallo. Los catálogos se piden con `xxxIDs[0]` — si el array viene
  // vacío el id es `undefined`, y si el maestro fue borrado la query resuelve
  // `null`; en ambos casos el `.name` lanzaba TypeError. Con `Promise.all` esa
  // promesa rechazada tumbaba EL LOTE ENTERO: tabla de comunicaciones en blanco
  // y el Excel de históricos cayendo al fallback DOM, que solo exporta la
  // página visible ("alguna fila no sale"). `allSettled` + los `?.` del objeto
  // de retorno descartan únicamente la fila irrecuperable. Happy path idéntico.
  const settled = await Promise.allSettled(
    communications.map(async (communication) => {
      const {
        data: { order },
      } = await client.query({
        query: GET_ORDER_BY_ID,
        variables: {
          id: communication.orderIDs[0],
        },
        fetchPolicy: reFetch ? 'network-only' : 'cache-first',
      });

      // Resiliencia ante datos huérfanos: la Order padre puede resolver a null
      // (Communication huérfana cuyo Order fue borrado → GET_ORDER_BY_ID = null).
      // Sin este guard, `order.predefinedOrderIDs`/`order.roid` reventaban más
      // abajo (TypeError) y, al no haber catch por-fila, el Promise.all
      // rechazaba TODA la tabla por una sola fila corrupta. Saltamos la fila
      // (se filtra abajo) y dejamos traza para depurar la integridad de datos.
      if (!order) {
        console.warn(
          `[selectCommunicationOrders] Order padre no resuelta (huérfana), fila omitida — communicationId=${communication.id} orderId=${communication.orderIDs?.[0]}`,
        );
        return null;
      }

      const [
        {
          data: { concession },
        },
        {
          data: { interlocutorGroup },
        },
        {
          data: { mediaOfCommunication },
        },
        {
          data: { road },
        },
      ] = await Promise.all([
        client.query({
          query: GET_CONCESSION_BY_ID,
          variables: {
            id: communication.concessionIDs[0],
          },
          fetchPolicy: reFetch ? 'network-only' : 'cache-first',
        }),
        client.query({
          query: GET_INTERLOCUTOR_GROUP_BY_ID,
          variables: {
            id: communication.interlocutorGroupIDs[0],
          },
          fetchPolicy: reFetch ? 'network-only' : 'cache-first',
        }),
        client.query({
          query: GET_MEDIA_OF_COMMUNICATION_BY_ID,
          variables: {
            id: communication.mediaOfCommunicationIDs[0],
          },
          fetchPolicy: reFetch ? 'network-only' : 'cache-first',
        }),
        client.query({
          query: GET_ROAD_BY_ID,
          variables: {
            id: communication.roadIDs[0],
          },
          fetchPolicy: reFetch ? 'network-only' : 'cache-first',
        }),
      ]);

      const predefinedOrderId = order.predefinedOrderIDs?.[0] || communication.predefinedOrderIDs?.[0];
      let predefinedOrderName = '';
      if (predefinedOrderId) {
        try {
          const cached = client.readQuery({ query: FETCH_PREDEFINED_ORDERS });
          predefinedOrderName = cached?.predefinedOrders?.find((p: any) => p.id === predefinedOrderId)?.name ?? '';
        } catch { /* cache not available */ }
      }

      return {
        id: order.id,
        roid: order.roid.split('.'),
        registerInitDate: order.registerInitDate,
        creationDate: order.creationDate,
        groupInter: interlocutorGroup?.name ?? '',
        media: mediaOfCommunication?.name ?? '',
        sendStatus: communication.sendStatus,
        concession: concession?.name ?? '',
        road: road?.name ?? '',
        pkInit: pkFormatter(communication.pkInitKm, communication.pkInitMeter),
        pkEnd: pkFormatter(communication.pkEndKm, communication.pkEndMeter),
        description: order.description,
        notes: order.notes,
        communicationID: communication.id,
        state: communication.state || order.state,
        predefinedOrder: predefinedOrderName,
        user: order.userId ? (creatorMap[order.userId] ?? '') : '',
        // Fase 4 nav Trabajo/Revisión (follow-up T4, 2026-07-12) — "Origen"
        // viene directo del campo GraphQL `Communication.containerType` (el
        // backend lo denormaliza en Prisma; ver NEW_COMMUNICATION fragment).
        // Ya no se deriva por fila con un lookup extra client-side.
        containerType: communication.containerType,
      };
    })
  );

  return settled
    .map((result, index) => {
      if (result.status === 'fulfilled') return result.value;
      console.warn(
        `[selectCommunicationOrders] Fila omitida por error irrecuperable — communicationId=${communications[index]?.id} orderId=${communications[index]?.orderIDs?.[0]}`,
        result.reason,
      );
      return null;
    })
    .filter((row): row is NonNullable<typeof row> => row != null);
};

/**
 * selectCommunicationById - Selector que obtiene los datos de una comunicación por su ID directo
 *
 * A diferencia de selectCommunicationOrderByIdOrder (que recibe orderId y busca la comunicación),
 * este selector recibe directamente el communicationId y obtiene la orden padre desde communication.orderIDs[0]
 *
 * Uso: Para navegación directa a comunicación desde ShowOT components
 * Ruta: /work-orders/communications/detail/:communicationId
 *
 * @param communicationId - ID de la comunicación
 * @param reFetch - Si debe refrescar los datos desde el servidor
 */
export const selectCommunicationById = async (
  communicationId: string,
  reFetch = false
) => {
  // 1. Obtener la comunicación directamente por su ID
  const {
    data: { communication },
  } = await client.query({
    query: GET_COMMUNICATION_BY_ID,
    variables: {
      id: communicationId,
    },
    fetchPolicy: reFetch ? 'network-only' : 'cache-first',
  });

  // 2. Obtener la orden desde communication.orderIDs[0]
  const {
    data: { order },
  } = await client.query({
    query: GET_ORDER_BY_ID,
    variables: {
      id: communication.orderIDs[0],
    },
    fetchPolicy: reFetch ? 'network-only' : 'cache-first',
  });

  // 3. Obtener la orden padre de expediente
  const parentExpeditionOrder = await client.query({
    query: GET_ORDER_BY_ID,
    variables: {
      id: order.parentExpeditionOrderIDs[0],
    },
    fetchPolicy: reFetch ? 'network-only' : 'cache-first',
  });

  // 4. Obtener datos relacionados en paralelo
  const [
    {
      data: { capitol },
    },
    {
      data: { subCapitol },
    },
    {
      data: { classification },
    },
    {
      data: { user },
    },
    {
      data: { predefinedOrders: predefinedOrdersList },
    },
  ] = await Promise.all([
    client.query({
      query: GET_CAPITOL_BY_ID,
      variables: {
        id: order.capitolIDs[0],
      },
      fetchPolicy: reFetch ? 'network-only' : 'cache-first',
    }),
    client.query({
      query: GET_SUB_CAPITOL_BY_ID,
      variables: {
        id: order.subCapitolIDs[0],
      },
      fetchPolicy: reFetch ? 'network-only' : 'cache-first',
    }),
    client.query({
      query: GET_CLASSIFICATION_BY_ID_OR_ROID,
      variables: {
        id: order.classificationIDs[0],
      },
      fetchPolicy: reFetch ? 'network-only' : 'cache-first',
    }),
    client.query({
      query: GET_USER_BY_ID,
      variables: {
        input: {
          id: order.userId,
        },
      },
      fetchPolicy: reFetch ? 'network-only' : 'cache-first',
    }),
    client.query({
      query: FETCH_PREDEFINED_ORDERS,
      fetchPolicy: 'cache-first',
    }),
  ]);

  // T36: Resolve predefinido name from ID (fallback to communication entity for backwards compatibility)
  const predefinedOrderId2 = order.predefinedOrderIDs?.[0] || communication.predefinedOrderIDs?.[0];
  const predefinedOrderName2 = predefinedOrderId2
    ? predefinedOrdersList?.find(
        (p: { id: string; name: string }) => p.id === predefinedOrderId2
      )?.name
    : undefined;

  // 5. Obtener datos de comunicación (concesión, carretera, interlocutor, medio)
  const [
    {
      data: { concession },
    },
    {
      data: { road },
    },
    {
      data: { interlocutorGroup },
    },
    {
      data: { mediaOfCommunication },
    },
  ] = await Promise.all([
    client.query({
      query: GET_CONCESSION_BY_ID,
      variables: {
        id: communication.concessionIDs[0],
      },
      fetchPolicy: reFetch ? 'network-only' : 'cache-first',
    }),
    client.query({
      query: GET_ROAD_BY_ID,
      variables: {
        id: communication.roadIDs[0],
      },
      fetchPolicy: reFetch ? 'network-only' : 'cache-first',
    }),
    client.query({
      query: GET_INTERLOCUTOR_GROUP_BY_ID,
      variables: {
        id: communication.interlocutorGroupIDs[0],
      },
      fetchPolicy: reFetch ? 'network-only' : 'cache-first',
    }),
    client.query({
      query: GET_MEDIA_OF_COMMUNICATION_BY_ID,
      variables: {
        id: communication.mediaOfCommunicationIDs[0],
      },
      fetchPolicy: reFetch ? 'network-only' : 'cache-first',
    }),
  ]);

  return {
    id: order.id,
    roid: order.roid.split('.'),
    registerInitDate: order.registerInitDate,
    groupInter: interlocutorGroup.name,
    media: mediaOfCommunication.name,
    sendStatus: communication.sendStatus,
    concession: concession.name,
    road: road.name,
    pkInit: pkFormatter(communication.pkInitKm, communication.pkInitMeter),
    pkEnd: pkFormatter(communication.pkEndKm, communication.pkEndMeter),
    description: order.description,
    notes: order.notes,
    capitol: capitol.name,
    subCapitol: subCapitol.name,
    classification: classification.name,
    predefine: predefinedOrderName2,
    track: trackLabel(communication.track),
    roadDirections: road.directions,
    direction: communication?.direction,
    margin: communication?.margin,
    lane: laneLabel(communication.lane),
    city: concession.location,
    pkInitRoad: pkFormatter(road.pkInitKm, road.pkInitMeter, true),
    pkEndRoad: pkFormatter(road.pkEndKm, road.pkEndMeter, true),
    state: communication.state || order.state,
    parentOrderId: order.parentExpeditionOrderIDs,
    userOperator: user,
    stateParentExpeditionOrder: parentExpeditionOrder.data.order.state,
  };
};
